import { Fragment } from "react";
import type { ComponentType } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { CalendarDays, ChevronDown, LayoutGrid, LayoutList } from "lucide-react";
import { command, isMultiRecordViewMode } from "../core";
import type { MenuEntry, MultiRecordViewMode, ViewMode } from "../core";
import { useWorkspace } from "../react/providers/WorkspaceProvider";
import { useWorkspaceNavigation } from "../react/navigation/useWorkspaceNavigation";
import { useCommand } from "../react/hooks/useCommand";
import { appIcon } from "./icons";
import { findCurrentAppId, findMenuAction, menuContains } from "./workspace-utils";

// Form is per-record (opened from a row), not a switchable mode — only the
// multi-record views appear in the trailing switcher.
const VIEW_META: Record<MultiRecordViewMode, { icon: ComponentType<{ size?: number }>; label: string }> = {
    list: { icon: LayoutList, label: "List" },
    kanban: { icon: LayoutGrid, label: "Kanban" },
    calendar: { icon: CalendarDays, label: "Calendar" },
};

// Secondary section-nav for the current app's menu tree. Leaves dispatch
// OPEN_ACTION; parents open a dropdown of their descendants.
export function ModuleMenuManager() {
    const boot = useWorkspace();
    const dispatch = useCommand();
    const { stack } = useWorkspaceNavigation();
    const activeKey = stack[0].actionKey;

    const appId = findCurrentAppId(boot, activeKey) ?? boot.apps[0]?.id;
    const app = boot.apps.find((a) => a.id === appId);
    const AppIcon = appIcon(app?.icon);
    const menus = appId ? boot.menus[appId] ?? [] : [];

    const open = (actionKey?: string, viewMode?: ViewMode) => {
        if (actionKey) void dispatch(command("OPEN_ACTION", { actionKey, viewMode }));
    };

    // Multi-record views the current action offers (list/kanban/calendar) — the
    // trailing switcher only shows when there's more than one to choose from.
    const activeAction = findMenuAction(menus, activeKey);
    const multiViews = (activeAction?.availableViews ?? []).filter(isMultiRecordViewMode);
    // Effective current view — walk the navigation stack to the topmost
    // frame. The ROOT frame's `viewMode` alone is wrong because a list
    // root can have a drilled-in record sitting on top (recordRef set on
    // the root, OR a RecordFrame / ListDrillFrame above it). Mirrors
    // ActionManager's `viewMode` resolution so the switcher self-hides
    // on any form view (root form, drilled record, list-drill with a row
    // opened) and only shows on a pure multi-record top frame.
    const activeView: ViewMode = (() => {
        const top = stack[stack.length - 1];
        if (top.kind === "record") return "form";
        if (top.kind === "list") return top.recordRef ? "form" : top.viewMode;
        // top is the root ActionFrame — viewMode is undefined for client
        // actions (no view switcher shows for them anyway since availableViews
        // is empty), so coalesce to "list".
        return top.recordRef ? "form" : (top.viewMode ?? "list");
    })();

    return (
        <nav className="section-nav" aria-label="Section">
            {app ? (
                <span className="section-nav__app-icon" data-color={app.color ?? 10} title={app.name}>
                    <AppIcon size={15} />
                </span>
            ) : null}
            {menus.map((menu) => {
                const active = menuContains(menu, activeKey);
                if (menu.children?.length) {
                    return (
                        <DropdownMenu.Root key={menu.id}>
                            <DropdownMenu.Trigger asChild>
                                <button className={`section-nav__item section-nav__item--has-menu ${active ? "is-active" : ""}`} data-viewid="ede-menu-item" data-menu-key={menu.id}>
                                    <span>{menu.name}</span>
                                    <ChevronDown size={12} className="section-nav__chev" />
                                </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                                <DropdownMenu.Content className="section-submenu" align="start" sideOffset={2}>
                                    <SubmenuItems items={menu.children} activeKey={activeKey} onOpen={open} />
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                    );
                }
                return (
                    <button
                        key={menu.id}
                        className={`section-nav__item ${active ? "is-active" : ""}`}
                        onClick={() => open(menu.actionKey, menu.action?.defaultView)}
                        data-viewid="ede-menu-item"
                        data-menu-key={menu.actionKey}
                    >
                        <span>{menu.name}</span>
                    </button>
                );
            })}

            {/* View-mode switcher renders only when the user is in a multi-
                record mode (list / kanban / calendar / pivot). On a single-
                record `form` view (drilled into one row), the switcher is
                irrelevant — flipping to list/kanban from a form would also
                throw away the drill context. The toolbar's back-arrow +
                pager handle navigation in that mode instead. */}
            {multiViews.length > 1 && activeView !== "form" ? (
                <>
                    <span className="section-nav__spacer" />
                    <div className="view-tabs">
                        {multiViews.map((v) => {
                            const meta = VIEW_META[v];
                            const Icon = meta.icon;
                            return (
                                <button
                                    key={v}
                                    type="button"
                                    className={`view-tabs__btn ${v === activeView ? "is-active" : ""}`}
                                    onClick={() => open(activeKey, v)}
                                    aria-label={`${meta.label} view`}
                                    aria-pressed={v === activeView}
                                    title={meta.label}
                                >
                                    <Icon size={16} />
                                </button>
                            );
                        })}
                    </div>
                </>
            ) : null}
        </nav>
    );
}

function SubmenuItems({
    items,
    activeKey,
    onOpen,
}: {
    items: MenuEntry[];
    activeKey: string;
    onOpen: (actionKey?: string, viewMode?: ViewMode) => void;
}) {
    // Group label is "TITLE ━━━━━━" — the trailing line IS the divider, so no
    // extra Separator between groups.
    return (
        <>
            {items.map((item) => {
                if (item.children?.length) {
                    return (
                        <Fragment key={item.id}>
                            <div className="section-submenu__group-label">{item.name}</div>
                            <SubmenuItems items={item.children} activeKey={activeKey} onOpen={onOpen} />
                        </Fragment>
                    );
                }
                return (
                    <DropdownMenu.Item
                        key={item.id}
                        className={`section-submenu__item ${item.actionKey === activeKey ? "is-active" : ""}`}
                        onSelect={() => onOpen(item.actionKey, item.action?.defaultView)}
                        data-viewid="ede-menu-item"
                        data-menu-key={item.actionKey}
                    >
                        {item.name}
                    </DropdownMenu.Item>
                );
            })}
        </>
    );
}
